import { useMutation } from '@apollo/client';
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  IconButton,
  IProgressIndicatorStyles,
  makeStyles,
  PrimaryButton,
  ProgressIndicator,
  Stack,
  TooltipHost,
} from '@fluentui/react';
import { showCustomToast } from 'common/components/CustomToast/utils';
import { ApprovalCopyInput } from 'common/types/globalTypes';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import {
  FormHookDropdown,
  FormHookTextField,
} from 'common/components/FormHooksFields';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { ApprovalSetupCommonData_approvalTypes } from 'settings/account/approvalSetup/__generated__/ApprovalSetupCommonData';
import {
  ApprovalsInfo,
  ApprovalsInfoVariables,
} from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalsInfo';
import { ApprovalValues } from '../../types';
import {
  ApprovalFlowCopy,
  ApprovalFlowCopyVariables,
} from './__generated__/ApprovalFlowCopy';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchema } from './approvalCopyValidation';
import { ApprovalCopyValues } from 'settings/account/approvalSetup/view/CopyApproval/types';
import { APPROVAL_COPY_INITIAL_VALUES } from 'settings/account/approvalSetup/view/CopyApproval/constants';
import { generateApprovalName } from 'settings/account/approvalSetup/view/CopyApproval/utils';

import CREATE_APPROVAL_COPY from './ApprovalFlowCopy.graphql';
import APPROVALS from '../../../ApprovalsInfo.graphql';

const progressIndicatorStyles: Partial<IProgressIndicatorStyles> = {
  root: { marginBottom: 10 },
};
interface CopyApprovalsProps {
  id: string;
  data: ApprovalSetupCommonData_approvalTypes;
  firstRowPriorityNumber: number;
}
const useStyles = makeStyles((theme) => ({
  iconButton: {
    marginRight: 10,
  },
}));
export const CopyApproval: React.FC<CopyApprovalsProps> = ({
  id,
  data,
  firstRowPriorityNumber,
}) => {
  const styles = useStyles();

  const history = useHistory();

  const formMethods = useForm<ApprovalCopyValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),

    defaultValues: APPROVAL_COPY_INITIAL_VALUES,
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = formMethods;

  const [showDialog, setShowDialog] = useState<boolean>(false);

  const approvalTypeOptions =
    data?.nodes.map((item) => ({
      key: item.id,
      text: item.approvalType || '',
    })) || [];
  const { watch } = useFormContext<ApprovalValues>();
  const values = watch();

  useEffect(() => {
    if (id && values.name && values.approvalTypeId) {
      const newInitialValues: ApprovalCopyValues = {
        ...APPROVAL_COPY_INITIAL_VALUES,
        approvalId: id,
        newApprovalTypeId: values.approvalTypeId,
        newName: generateApprovalName(values.name),
      };

      reset(newInitialValues); // this updates the form with derived defaults
    }
  }, [id, values.name, values.approvalTypeId, reset]);
  const [createApprovalCopy, { loading: createApprovalCopyLoading }] =
    useMutation<ApprovalFlowCopy, ApprovalFlowCopyVariables>(
      CREATE_APPROVAL_COPY,
      {
        errorPolicy: 'all',
      }
    );

  const onHandleSubmit = async (values: ApprovalCopyValues) => {
    const { errors, data } = await createApprovalCopy({
      variables: {
        input: {
          ...(values as ApprovalCopyInput),
          newApprovalTypeId: Number(values.newApprovalTypeId),
          newPriorityNumber: -999,
        },
      },
      update: (cache, { data }) => {
        const cacheData = cache.readQuery<
          ApprovalsInfo,
          ApprovalsInfoVariables
        >({
          query: APPROVALS,
        });
        if (
          cacheData?.approvals?.pageInfo &&
          cacheData?.approvals?.totalCount &&
          data?.approvalCopy?.approval
        ) {
          const newData: ApprovalsInfo = {
            approvals: {
              ...cacheData.approvals,
              pageInfo: cacheData.approvals.pageInfo,
              totalCount: cacheData.approvals.totalCount,
              nodes: [
                data?.approvalCopy.approval,
                ...cacheData.approvals.nodes,
              ],
            },
          };
          cache.writeQuery<ApprovalsInfo, ApprovalsInfoVariables>({
            query: APPROVALS,
            data: newData,
          });
        }
      },
    });
    if (errors?.length) {
      showCustomToast({
        content: errors[0].message,
        appearance: 'error',
      });
    }
    if (data?.approvalCopy?.approval) {
      setShowDialog(false);
      history.replace(
        `/account-management/approval-setup?id=${data.approvalCopy.approval.id}`
      );
      showCustomToast({
        content: 'Approval copy added successfully',
        appearance: 'success',
      });
    }
  };

  useEffect(() => {
    if (id && values.name && values.approvalTypeId) {
      const newInitialValues: ApprovalCopyValues = {
        ...APPROVAL_COPY_INITIAL_VALUES,
        approvalId: id,
        newApprovalTypeId: values.approvalTypeId,
        newName: generateApprovalName(values.name),
      };
      reset(newInitialValues); // this updates the form with derived defaults
    }
  }, [id, values.name, values.approvalTypeId, reset]);
  return (
    <>
      <TooltipHost content="Clone">
        <IconButton
          iconProps={{ iconName: 'Copy' }}
          ariaLabel="Copy"
          onClick={() => setShowDialog(true)}
          className={styles.iconButton}
        />
      </TooltipHost>
      {showDialog && (
        <Dialog
          hidden={false}
          onDismiss={() => setShowDialog(false)}
          dialogContentProps={{
            title: 'Clone Approval',
            type: DialogType.largeHeader,
          }}
          minWidth={500}
        >
          <FormProvider {...formMethods}>
            <Stack tokens={{ childrenGap: 10 }}>
              <FormHookDropdown
                label="Approval Type"
                placeholder="Select"
                options={approvalTypeOptions}
                name="newApprovalTypeId"
                required
              />
              <FormHookTextField
                name="newName"
                label="Name"
                placeholder="Enter name"
                required
              />
            </Stack>
            <DialogFooter>
              {createApprovalCopyLoading && (
                <ProgressIndicator styles={progressIndicatorStyles} />
              )}

              <PrimaryButton
                disabled={!!Object.keys(errors).length || isSubmitting}
                text="Confirm"
                onClick={async () => {
                  handleSubmit(onHandleSubmit, () => {})();
                }}
              />
              <DefaultButton
                onClick={() => setShowDialog(false)}
                text="Cancel"
              />
            </DialogFooter>
          </FormProvider>
        </Dialog>
      )}
    </>
  );
};
